import {
  JxtAlert,
  JxtAlertTypeEnum,
  JxtFloorExceptionalClosingHoursDetails,
  JxtLoader,
  JxtOffCanvasHeaderHideIconEnum,
} from '@jooxter/ui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useOffCanvasStore } from '../../store';
import { IRange, OffCanvasModeEnum, createGTMGAEvent, fromISO, isSameTimezoneAsTheUser, now } from '@jooxter/utils';
import { useTimezoneNameFormatter } from '../useTimezoneNameFormatter';
import { useFetchFloorExceptionalClosingHours } from '../../queries';
import { useFetchFloor } from '@jooxter/floors';
import { capitalize } from 'lodash-es';
import { useFloorExceptionalClosingHoursToIJxtFloorExceptionalClosingHoursDetailsAdapter } from '../../adapters/floor-exceptional-closing-hours/useFloorExceptionalClosingHoursToIJxtFloorExceptionalClosingHoursDetailsAdapter';
import { useFloorExceptionalClosingHoursActions } from './useFloorExceptionalClosingHoursActions';
// eslint-disable-next-line import/no-named-as-default
import JxtFloorExceptionalClosingHoursFormContainer from '../../components/JxtFloorExceptionalClosingHoursFormContainer';
import { IOffCanvasContent } from '../offCanvasContent.types';

// Reads the floor slice of the off-canvas store and turns it into the props JxtOffCanvas needs -
// the floor counterpart of useBookingOffCanvasContent, mounted once at the off-canvas host.
export const useFloorOffCanvasContent = (): IOffCanvasContent => {
  const { format: formatTimezoneName } = useTimezoneNameFormatter();
  const { mode, floorId, floorExceptionalClosingHoursId, preset, selectedBuilding } = useOffCanvasStore(
    (state) => state.floor
  );
  const { close, floorGoToEditView, setFloorMode } = useOffCanvasStore((state) => ({
    close: state.close,
    floorGoToEditView: state.floorGoToEditView,
    setFloorMode: state.setFloorMode,
  }));
  const { t } = useTranslation();
  const { floorExceptionalClosingHours } = useFetchFloorExceptionalClosingHours(floorExceptionalClosingHoursId);
  const { floor } = useFetchFloor(floorId ?? floorExceptionalClosingHours?.id);
  const { floorExceptionalClosingHoursDetails } =
    useFloorExceptionalClosingHoursToIJxtFloorExceptionalClosingHoursDetailsAdapter(
      selectedBuilding,
      floorExceptionalClosingHours
    );
  const bookingRange: IRange | undefined = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    if (floorExceptionalClosingHours && fromISO(floorExceptionalClosingHours.timestampTo!) > now()) {
      return {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        from: fromISO(floorExceptionalClosingHours.timestampFrom!),
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        to: fromISO(floorExceptionalClosingHours.timestampTo!),
      };
    }
    return undefined;
  }, [floorExceptionalClosingHours]);
  const { cancel, deleteEventModal } = useFloorExceptionalClosingHoursActions({
    floorId,
    range: bookingRange,
    floorExceptionalClosingHours,
    floorExceptionalClosingHoursDetails,
  });

  const floorTimezone = selectedBuilding?.timezone;
  const shouldShowTimezoneWarning = floorTimezone ? !isSameTimezoneAsTheUser(floorTimezone) : false;

  const resetAndCloseOffCanvas = () => {
    if (mode) {
      createGTMGAEvent('ManageSpaces', capitalize(mode), 'Floor Close Button');
    }
    close();
  };

  const getIconList = (): { iconName: string; title: string; onClick: () => void }[] => {
    const icons: { iconName: string; title: string; onClick: () => void }[] = [];

    if (OffCanvasModeEnum.View !== mode || !floorExceptionalClosingHours) {
      return icons;
    }

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    if (fromISO(floorExceptionalClosingHours.timestampFrom!).plus({ minutes: 5 }) > now()) {
      icons.push({
        iconName: 'pen',
        title: t<string>('update'),
        onClick: () => {
          floorGoToEditView();
          createGTMGAEvent('ManageSpaces', 'Update', 'Floor Modify Button');
        },
      });
    }

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    if (fromISO(floorExceptionalClosingHours.timestampTo!) > now()) {
      icons.push({
        iconName: 'trash',
        title: t<string>('delete'),
        onClick: () => {
          cancel();
          createGTMGAEvent('ManageSpaces', 'Update', 'Floor Delete Button');
        },
      });
    }

    return icons;
  };

  return useMemo(
    () => ({
      header: {
        hideIcon:
          mode === OffCanvasModeEnum.Update ? JxtOffCanvasHeaderHideIconEnum.Back : JxtOffCanvasHeaderHideIconEnum.Close,
        iconList: getIconList(),
        title:
          mode !== OffCanvasModeEnum.View ? (
            <header className="max-w-full text-center">
              <h3 className="text-neutral-140 font-medium truncate">{t<string>('manage-floor.offcanvas.header.title')}</h3>
            </header>
          ) : null,
        children: mode !== OffCanvasModeEnum.View && (
          <>
            {shouldShowTimezoneWarning && floorTimezone && (
              <JxtAlert text={formatTimezoneName(floorTimezone)} type={JxtAlertTypeEnum.Warning} closeable={false} />
            )}
          </>
        ),
      },
      onHide: () => {
        if (mode === OffCanvasModeEnum.Update) {
          setFloorMode(OffCanvasModeEnum.View);
        } else {
          resetAndCloseOffCanvas();
        }
      },
      bodyClass: 'overflow-y-auto',
      children: (
        <>
          {mode === OffCanvasModeEnum.View && !!floorExceptionalClosingHoursDetails && (
            <JxtFloorExceptionalClosingHoursDetails {...floorExceptionalClosingHoursDetails} />
          )}
          {selectedBuilding && (
            <>
              {mode === OffCanvasModeEnum.Create && (
                <JxtFloorExceptionalClosingHoursFormContainer
                  floorExceptionalClosingHoursFormPreset={{ ...preset, floor }}
                  selectedBuilding={selectedBuilding}
                />
              )}
              {mode === OffCanvasModeEnum.Update && (
                <JxtFloorExceptionalClosingHoursFormContainer
                  floorExceptionalClosingHoursToEdit={floorExceptionalClosingHours}
                  selectedBuilding={selectedBuilding}
                />
              )}
            </>
          )}
          {!(floorExceptionalClosingHours || floor) && (
            <div className="absolute top-0 right-0 left-0 bottom-0 backdrop-blur-sm h-full flex flex-1 items-center justify-center">
              <JxtLoader />
            </div>
          )}
          {deleteEventModal}
        </>
      ),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      mode,
      formatTimezoneName,
      floorExceptionalClosingHoursDetails,
      selectedBuilding,
      preset,
      floor?.id,
      floorExceptionalClosingHours,
      deleteEventModal,
    ]
  );
};
